/* eslint-disable no-param-reassign */
/* eslint-disable fp/no-mutation */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'

type Course = {
  id: string
  kind: 'SelfPaced' | 'Live'
  name: string
  displayName: string
  courseCode?: string
  imageName: string
}

type DemographicBreakdown = {
  licenseType?: string
  licenseState?: string
  count: number
}

type LicenseTypeDemographics = {
  all: DemographicBreakdown[]
  active: DemographicBreakdown[]
  subscribed: DemographicBreakdown[]
  linkedService: DemographicBreakdown[]
}

type LicenseStateDemographics = {
  all: DemographicBreakdown[]
  active: DemographicBreakdown[]
  subscribed: DemographicBreakdown[]
  linkedService: DemographicBreakdown[]
}

type ScribeSlice = {
  coursesStatus: Status
  courses: Course[]
  selectedCourse: string
  licenseTypeDemographics: LicenseTypeDemographics
  licenseStateDemographics: LicenseStateDemographics
  licenseTypeDemographicsStatus: Status
  licenseStateDemographicsStatus: Status
}

export let initialState: ScribeSlice = {
  coursesStatus: 'uninitialized',
  courses: [],
  selectedCourse: '',
  licenseTypeDemographics: {
    all: [],
    active: [],
    subscribed: [],
    linkedService: [],
  },
  licenseStateDemographics: {
    all: [],
    active: [],
    subscribed: [],
    linkedService: [],
  },
  licenseTypeDemographicsStatus: 'uninitialized',
  licenseStateDemographicsStatus: 'uninitialized',
}

export let fetchCourses = createAsyncThunk('scribe/fetchCourses', () =>
  axios.get<Course[]>('/api/scribe/courses').then(pluckData),
)

export let fetchLicenseTypeDemographics = createAsyncThunk(
  'scribe/fetchLicenseTypeDemographics',
  async () => {
    let [all, active, subscribed, linkedService] = await Promise.all([
      axios
        .get<
          DemographicBreakdown[]
        >('/api/scribe/users/demographics/license-type/all')
        .then(pluckData),
      axios
        .get<
          DemographicBreakdown[]
        >('/api/scribe/users/demographics/license-type/active')
        .then(pluckData),
      axios
        .get<
          DemographicBreakdown[]
        >('/api/scribe/users/demographics/license-type/subscribed')
        .then(pluckData),
      axios
        .get<
          DemographicBreakdown[]
        >('/api/scribe/users/demographics/license-type/linked-service')
        .then(pluckData),
    ])
    return { all, active, subscribed, linkedService }
  },
)

export let fetchLicenseStateDemographics = createAsyncThunk(
  'scribe/fetchLicenseStateDemographics',
  async () => {
    let [all, active, subscribed, linkedService] = await Promise.all([
      axios
        .get<
          DemographicBreakdown[]
        >('/api/scribe/users/demographics/license-state/all')
        .then(pluckData),
      axios
        .get<
          DemographicBreakdown[]
        >('/api/scribe/users/demographics/license-state/active')
        .then(pluckData),
      axios
        .get<
          DemographicBreakdown[]
        >('/api/scribe/users/demographics/license-state/subscribed')
        .then(pluckData),
      axios
        .get<
          DemographicBreakdown[]
        >('/api/scribe/users/demographics/license-state/linked-service')
        .then(pluckData),
    ])
    return { all, active, subscribed, linkedService }
  },
)

export let scribeSlice = createSlice({
  name: 'scribe',
  initialState,
  reducers: {
    coursesStatusChanged: (state, action: PayloadAction<Status>) => {
      state.coursesStatus = action.payload
    },
    coursesChanged: (state, action: PayloadAction<Course[]>) => {
      state.courses = action.payload
    },
    selectedCourseChanged: (state, action: PayloadAction<string>) => {
      state.selectedCourse = action.payload
    },
    licenseTypeDemographicsStatusChanged: (
      state,
      action: PayloadAction<Status>,
    ) => {
      state.licenseTypeDemographicsStatus = action.payload
    },
    licenseStateDemographicsStatusChanged: (
      state,
      action: PayloadAction<Status>,
    ) => {
      state.licenseStateDemographicsStatus = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCourses.pending, (state) => {
        state.coursesStatus = 'pending'
      })
      .addCase(fetchCourses.rejected, (state) => {
        state.coursesStatus = 'rejected'
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.coursesStatus = 'fulfilled'
        state.courses = action.payload
      })
      .addCase(fetchLicenseTypeDemographics.pending, (state) => {
        state.licenseTypeDemographicsStatus = 'pending'
      })
      .addCase(fetchLicenseTypeDemographics.rejected, (state) => {
        state.licenseTypeDemographicsStatus = 'rejected'
      })
      .addCase(fetchLicenseTypeDemographics.fulfilled, (state, action) => {
        state.licenseTypeDemographicsStatus = 'fulfilled'
        state.licenseTypeDemographics = action.payload
      })
      .addCase(fetchLicenseStateDemographics.pending, (state) => {
        state.licenseStateDemographicsStatus = 'pending'
      })
      .addCase(fetchLicenseStateDemographics.rejected, (state) => {
        state.licenseStateDemographicsStatus = 'rejected'
      })
      .addCase(fetchLicenseStateDemographics.fulfilled, (state, action) => {
        state.licenseStateDemographicsStatus = 'fulfilled'
        state.licenseStateDemographics = action.payload
      })
  },
})

export let {
  coursesStatusChanged,
  coursesChanged,
  selectedCourseChanged,
  licenseTypeDemographicsStatusChanged,
  licenseStateDemographicsStatusChanged,
} = scribeSlice.actions

export let selectCoursesStatus = (state: AppRootState) =>
  state.scribe.coursesStatus
export let selectCourses = (state: AppRootState) => state.scribe.courses
export let selectCourseByID = (id: string) => (state: AppRootState) =>
  state.scribe.courses.find((course) => course.id === id)

export let selectSelectedCourse = (state: AppRootState) =>
  state.scribe.selectedCourse

export let selectLicenseTypeDemographics = (state: AppRootState) =>
  state.scribe.licenseTypeDemographics
export let selectLicenseStateDemographics = (state: AppRootState) =>
  state.scribe.licenseStateDemographics
export let selectLicenseTypeDemographicsStatus = (state: AppRootState) =>
  state.scribe.licenseTypeDemographicsStatus
export let selectLicenseStateDemographicsStatus = (state: AppRootState) =>
  state.scribe.licenseStateDemographicsStatus

export default scribeSlice.reducer
